/**
 * @fileoverview Toolbar for managing company list views (filters, sorting, columns, saving).
 * @description Provides controls for selecting, saving, updating, and deleting custom views,
 * as well as applying filters, sorting, and managing visible columns for the company list.
 * Handles authentication checks for view modification actions
 * @active Client component [CONFIRMED]. Part of the company list feature
 *
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/index.tsx} - Parent toolbar component
 * @see {@link src/providers/supabaseAuthProvider.client.tsx} - Used for checking auth state
 * @see {@link src/hooks/useGetCurrentView.ts} - Hook to get current view state
 * @see {@link src/hooks/useViewFilterFeatures.ts} - Hook for filter button logic
 * @see {@link src/services/newView.service.ts} - RTK Query hooks for view operations
 * @see {@link src/store/newView/newView.selectors.ts} - Selector for all views
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/view/ViewMenu.tsx} - Dropdown for selecting views.
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/view/SaveMenu.tsx} - Dropdown for saving/updating views.
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/view/SortCompaniesMenu.tsx} - Dropdown for sorting.
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/view/HideColumnMenu.tsx} - Dropdown for column visibility.
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/view/ViewFilterModal.tsx} - Modal for applying filters.
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/view/AddViewModal.tsx} - Modal for adding new views.
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/DeleteNewViewModal.tsx} - Modal for deleting views.
 * @see {@link src/components/ui/WaitlistOverlay.tsx} - Waitlist overlay for early access
 */

import { useMemo, useState } from "react";

import { Flex, Icon, useDisclosure } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import FilterIconSvg from "@/assets/svg/filter-icon.svg";
import InfoIcon from "@/assets/svg/list-info-icon.svg";
import RegisterModal from "@/components/companies/companiesList/newCompaniesList/viewListTools/RegisterModal";
import WaitlistOverlay from "@/components/ui/WaitlistOverlay";
import { useActions } from "@/hooks/useActions";
import useCompaniesListRequest from "@/hooks/useCompaniesListRequest";
import useGetCurrentView from "@/hooks/useGetCurrentView";
import useViewFilterFeatures from "@/hooks/useViewFilterFeatures";
import { useSupabaseAuth } from "@/providers/supabaseAuthProvider.client"; // Import the new hook
import {
  useDeleteNewViewMutation,
  useLazyGetViewByIdQuery,
  useUpdateNewViewMutation,
} from "@/services/newView.service";
// Removed getUserIsVerify import
import newViewSelectors from "@/store/newView/newView.selectors";
// Import View type from the central types file
import type { View } from "@/types/view";

import DeleteNewViewModal from "../DeleteNewViewModal";
import ListText from "../shared/ListText";
import PreferenceButton from "../shared/PreferenceButton";
import ViewPreferenceItem from "../shared/ViewPreferenceItem";
import ViewMenu from "../viewMenu";

import AddViewModal from "./AddViewModal";
import HideColumnMenu from "./hideColumnMenu";
import SaveMenu from "./saveMenu";
import SortCompaniesMenu from "./sortCompaniesMenu";
import ViewFilterModal from "./ViewFilterModal";

/**
 * Default view configuration
 * 
 * This is the default view that is created when a user first logs in
 * It is used to display the company list in the default view
 * 
 */
const defaultView = [
  {
    id: "default",
    title: "Default",
    jsonFilterCriteria: {},
    resObject: "res_entity",
  },
];

/**
 * Renders the toolbar section specifically for managing views (select, save, filter, sort, columns)
 * Integrates various menus and modals for view-related actions
 * It also includes a temporary waitlist CTA button for non-logged in users
 *
 * @component CompaniesView
 * @returns {JSX.Element} The rendered view management toolbar.
 */
const CompaniesView = (): JSX.Element => {
  const controlModal = useDisclosure(); // Filter modal control
  const controlAddViewModal = useDisclosure(); // Add view modal control
  const regModalControl = useDisclosure(); // Registration modal control
  const deleteViewModalControl = useDisclosure();
  // Add state for waitlist overlay
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  
  const {
    setCurrentNewView,
    setArrayFilters,
    setViewParams,
    setIsLoadingCompanies,
    clearSelectedCompanies,
  } = useActions();
  const { user, isLoading: isAuthLoading } = useSupabaseAuth(); // Use the new hook
  const isLoggedIn = !!user && !isAuthLoading; // Derive isLoggedIn
  const { currentView, currentSavedParams, viewParams, currentParams } =
    useGetCurrentView();
  const views = useSelector(newViewSelectors.getAllViews());

  // Explicitly type the state to allow null or a View object
  const [selectedViewtoDelete, setSelectedViewToDelete] = useState<View | null>(
    null
  );
  const { getCompanies } = useCompaniesListRequest();
  const [updateNewView] = useUpdateNewViewMutation();
  const [getViewById] = useLazyGetViewByIdQuery();
  const [deleteNewView] = useDeleteNewViewMutation();

  const allViews = useMemo(() => {
    return [...defaultView, ...views];
  }, [views]);

  const removeUnnecessaryKeys = (obj) => {
    // Filter out the 'text' key which might be added by search components
    return Object.fromEntries(
      Object.entries(obj).filter(([key]) => key !== "text")
    );
  };

  // Clean current and saved parameters for comparison
  const params = currentParams ? removeUnnecessaryKeys(currentParams) : null;
  const savedParams = currentSavedParams
    ? removeUnnecessaryKeys(currentSavedParams)
    : null;

  // Determine if the current parameters differ from the saved view's parameters
  const isSave = JSON.stringify(savedParams) !== JSON.stringify(params);

  // Hook for managing filter button state and actions
  const { filterBtnTitle, onOpenFilterHandler, removeFilterHandler } =
    useViewFilterFeatures({
      getCompanies,
      currentParams,
      currentView,
      controlModal,
      clearSelectedCompanies,
      setArrayFilters,
      setViewParams,
    });

  /**
   * Handles selecting a different view from the ViewMenu.
   * Fetches the selected view's data and updates the company list.
   *
   * @function onSelectCurrentViewHandler
   * @param {object} view - The selected view object.
   */
  const onSelectCurrentViewHandler = (view: { id: string }): void => {
    clearSelectedCompanies();
    if (view?.id === currentView?.id) return; // Do nothing if same view selected

    const isDefaultView = view?.id === "default";
    setIsLoadingCompanies(true);
    setCurrentNewView(view);
    !isDefaultView
      ? getViewById(view?.id).then((res) => {
          setIsLoadingCompanies(false);
          if (res?.data?.success) {
            // Remove 'params' nesting
            getCompanies({
              ...res?.data?.data?.jsonFilterCriteria,
              orderBy: res?.data?.data?.jsonFilterCriteria?.orderBy?.value,
              limit: res?.data?.data?.limit || 25,
              page: 1,
            });
          }
        })
      : (setIsLoadingCompanies(false),
        // Remove 'params' nesting
        getCompanies({
          ...viewParams?.["default"],
          orderBy: viewParams?.["default"]?.orderBy?.value,
          limit: viewParams?.["default"]?.limit || 25, // Ensure limit and page are passed if needed
          page: 1,
        }));
  };

  /**
   * Opens the SaveMenu if logged in, otherwise opens the waitlist overlay.
   *
   * @function onOpenSaveMenuHandler
   * @param {Function} onOpen - The function to open the SaveMenu (passed from the menu component).
   */
  const onOpenSaveMenuHandler = (onOpen: () => void): void => {
    isLoggedIn ? onOpen() : setIsWaitlistOpen(true);
  };

  /**
   * Handles saving (updating) the currently selected custom view with the current parameters.
   * Triggers the `updateNewView` mutation if logged in.
   *
   * @function onSaveHandler
   * @param {object} currView - The view object to save/update.
   */
  const onSaveHandler = (currView: { id: string }): void => {
    const { ...restParams } = currentParams; // Get current filter/sort params
    const options = {
      id: currView?.id, // ID of the view to update
      params: {
        // Payload for the update mutation
        resObject: "res_entity", // TODO: Confirm if this is still needed/correct
        jsonFilterCriteria: {
          ...restParams, // Include all current parameters
        },
      },
    };

    if (isLoggedIn) {
      updateNewView(options); // Call the RTK Query mutation
    } else {
      setIsWaitlistOpen(true); // Open waitlist overlay if not authenticated
    }
  };

  /**
   * Opens the delete confirmation modal and sets the view to be deleted.
   *
   * @function onDeleteViewModalOpen
   * @param {View | null} view - The view object selected for deletion, or null.
   */
  const onDeleteViewModalOpen = (view: View | null): void => {
    deleteViewModalControl.onOpen();
    setSelectedViewToDelete(view); // Store the view (or null)
  };

  /**
   * Handles the actual deletion of the selected view after confirmation.
   * Triggers the `deleteNewView` mutation.
   *
   * @function onDeleteViewHandler
   */
  const onDeleteViewHandler = (): void => {
    // Add null check before accessing id
    if (selectedViewtoDelete?.id) {
      deleteNewView(selectedViewtoDelete.id); // Call RTK Query mutation
    }
    deleteViewModalControl.onClose(); // Close the confirmation modal
  };

  return (
    <>
      {/* Conditionally render modal only when an item is selected */}
      {selectedViewtoDelete && (
        <DeleteNewViewModal
          deleteViewModalControl={deleteViewModalControl}
          deleteHandler={onDeleteViewHandler}
          selectedItemToDelete={selectedViewtoDelete}
        />
      )}
      {/* Replace RegisterModal with WaitlistOverlay */}
      <WaitlistOverlay
        isOpen={isWaitlistOpen}
        onClose={() => setIsWaitlistOpen(false)}
      />
      <ViewFilterModal controlModal={controlModal} />
      <AddViewModal
        onClose={controlAddViewModal.onClose}
        isOpen={controlAddViewModal.isOpen}
        title={"Add view"}
        isView={true}
      />
      <Flex
        w={"100%"}
        align={{
          base: "start",
        }}
        justify={"start"}
        minH={"40px"}
        px={"8px"}
        py={"4px"}
        borderRadius={"8px"}
        bg={"lightBlue2"}
        columnGap={"8px"}
        direction={{
          base: "column",
          forCard2: "row",
          smx: "column",
          xl: "row",
        }}
        rowGap={"8px"}
      >
        <Flex align={"center"} columnGap={"8px"} w={"100%"} maxW={"376px"}>
          <Flex align={"center"} h={"32px"}>
            <ListText title={"View"} />
            <Icon as={InfoIcon} alignSelf={"flex-start"} />
          </Flex>
          <ViewMenu
            isView={true}
            selected={currentView}
            onAddOpen={controlAddViewModal.onOpen}
            listData={allViews}
            listHandler={onSelectCurrentViewHandler}
            deleteHandler={onDeleteViewModalOpen}
          />
        </Flex>
        <Flex
          align={"center"}
          columnGap={"10px"}
          w={"100%"}
          maxW={isSave ? "530px" : "100%"}
          flexWrap={"wrap"}
          rowGap={"4px"}
        >
          <PreferenceButton
            color={"lightGreen"}
            isActive={controlModal.isOpen || currentParams?.arrayFilters}
            isClose={currentParams?.arrayFilters}
            closeHandler={removeFilterHandler}
          >
            <ViewPreferenceItem
              title={filterBtnTitle}
              icon={FilterIconSvg}
              handler={onOpenFilterHandler}
            />
          </PreferenceButton>

          <SortCompaniesMenu />
          <HideColumnMenu />
          <Flex align={"center"} w={"94px"}>
            {isSave ? (
              <SaveMenu
                listHandler={onSaveHandler}
                listData={views}
                onOpenMenuHandler={onOpenSaveMenuHandler}
              />
            ) : null}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default CompaniesView;
